import React, { useState, useEffect, useCallback } from "react";
import checkPermission from "../Utils/checkPermission";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  Alert,
} from "@mui/material";
import axiosApi from "../Utils/axiosApi";

const ExpenseFormDialog = ({
  open,
  onClose,
  onSave,
  expense,
  hostelId,
  hostelName,
  hostelerId,
  hostelerName,
}) => {
  const [formData, setFormData] = useState({
    hostelerId,
    hostelId,
    hostelerName,
    hostelName,
    expenseStartDate: "",
    expenseEndDate: "",
    expenseType: "",
    expenseDetails: "",
    additionalExpenseAmount: "",
  });

  const [message, setMessage] = useState({ text: "", severity: "" });

  // ✅ Memoize resetForm to avoid eslint dependency warnings
  const resetForm = useCallback(() => {
    setFormData({
      hostelerId,
      hostelId,
      hostelerName,
      hostelName,
      expenseStartDate: "",
      expenseEndDate: "",
      expenseType: "",
      expenseDetails: "",
      additionalExpenseAmount: "",
    });
  }, [hostelerId, hostelId, hostelerName, hostelName]);

  useEffect(() => {
    if (expense) {
      setFormData({
        ...expense,
        hostelerId,
        hostelId,
        hostelerName,
        hostelName,
      });
    } else {
      resetForm();
    }
    setMessage({ text: "", severity: "" }); // clear message when opening dialog
  }, [expense, hostelId, hostelName, hostelerId, hostelerName, resetForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const payloadData = { ...formData, hostelId, hostelerId };

    // ✅ Basic validation
    if (!payloadData.expenseStartDate || !payloadData.expenseEndDate) {
      setMessage({ text: "Start and End date required.", severity: "error" });
      return;
    }
    if (new Date(payloadData.expenseEndDate) < new Date(payloadData.expenseStartDate)) {
      setMessage({ text: "End date cannot be before start date.", severity: "error" });
      return;
    }
    if (!payloadData.expenseType) {
      setMessage({ text: "Expense Type required.", severity: "error" });
      return;
    }
    if (
      payloadData.additionalExpenseAmount === "" ||
      isNaN(Number(payloadData.additionalExpenseAmount))
    ) {
      setMessage({ text: "Valid Expense Amount required.", severity: "error" });
      return;
    }

    try {
      if (formData.id) {
        await axiosApi.post(
          "/hostelerAdditionalExpense/updateHostelerAdditionalExpense",
          { hostelerAdditionalExpenseDTO: payloadData }
        );
      } else {
        await axiosApi.post(
          "/hostelerAdditionalExpense/createHostelerAdditionalExpense",
          { hostelerAdditionalExpenseDTO: payloadData }
        );
      }

      if (onSave) onSave(payloadData);

      setMessage({ text: "Expense saved successfully!", severity: "success" });
      resetForm();
      // ❌ Don't close here automatically — let user decide
    } catch (err) {
      console.error("Error saving expense:", err);
      setMessage({ text: "Failed to save expense.", severity: "error" });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // prevent closing on backdrop or ESC
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Additional Expense Form</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Inline success/error message */}
          {message.text && <Alert severity={message.severity}>{message.text}</Alert>}

          <TextField
            label="Hosteler Name"
            value={formData.hostelerName}
            fullWidth
            disabled
          />
          <TextField
            label="Hostel Name"
            value={formData.hostelName}
            fullWidth
            disabled
          />

          <TextField
            label="Expense Start Date"
            name="expenseStartDate"
            type="date"
            value={formData.expenseStartDate}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Expense End Date"
            name="expenseEndDate"
            type="date"
            value={formData.expenseEndDate}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Expense Type"
            name="expenseType"
            value={formData.expenseType}
            onChange={handleChange}
            fullWidth
          >
            <MenuItem value="Food">Food</MenuItem>
            <MenuItem value="Damage">Damage</MenuItem>
            <MenuItem value="Late Fee">Late Fee</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField
            label="Description"
            name="expenseDetails"
            value={formData.expenseDetails}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            label="Additional Expense Amount"
            name="additionalExpenseAmount"
            type="number"
            value={formData.additionalExpenseAmount}
            onChange={handleChange}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={!checkPermission("Hostel", "Hosteler List", "UPDATEADDEXP")}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseFormDialog;
